/**
 * The notification feed's API surface, resolved through `lib/api/config.ts`.
 *
 * WHY THIS IS NOT AN `API_DOMAINS` ENTRY (yet). The domain map is the right
 * home for `/py-api/v1/notifications`, and promoting this constant into it is
 * a one-line change:
 *
 *     notifications: { backend: "fastapi", root: "/notifications" },
 *
 * after which `apiUrl("notifications", …)` replaces `notificationUrl` here.
 * It was not added in this wave because `config.ts` is being edited
 * concurrently by other agents and this feature has no need to contend for it.
 * The property the map exists to provide — NO HARDCODED PREFIX — still holds:
 * `BACKEND_PREFIX` and `API_HOST` come from the map's own module, so the day
 * this moves backends it is the same one word here as it would be there. Same
 * escape hatch, same reasoning, as `pages/recycle-bin/api.ts`.
 */
import {
  API_HOST,
  BACKEND_PREFIX,
  type DomainConfig,
  type QueryValue,
} from "@/lib/api/config";
import { apiFetch } from "@/lib/api/client";

/** Shaped exactly like an `API_DOMAINS` row, so promoting it is a copy-paste. */
const NOTIFICATIONS: DomainConfig = { backend: "fastapi", root: "/notifications" };

/** `apiUrl()` for the one domain that is not in the map yet. */
export function notificationUrl(path = "", query?: Record<string, QueryValue>): string {
  const suffix = path && !path.startsWith("/") ? `/${path}` : path;
  const base = `${API_HOST}${BACKEND_PREFIX[NOTIFICATIONS.backend]}${NOTIFICATIONS.root}${suffix}`;
  if (!query) return base;

  const parts: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `${base}?${parts.join("&")}` : base;
}

// ── Contract ────────────────────────────────────────────────────────────────

/** Mirrors `NotificationKind` in `backend/api/app/services/notifications.py`. */
export type NotificationKind =
  | "RENT_DUE"
  | "RENT_REMINDER"
  | "REPORT"
  | "BACKUP"
  | "EXPIRY"
  | "ACCESS_REQUEST"
  | "FAMILY_ACTIVITY";

/** Mirrors `Severity` in the same module. */
export type NotificationSeverity = "INFO" | "SUCCESS" | "WARNING" | "CRITICAL";

export interface NotificationItem {
  /** `"<source>:<row-id>"`, deterministic — see the service module docstring. */
  id: string;
  kind: NotificationKind;
  severity: NotificationSeverity;
  title: string;
  /**
   * Detail line, or `null`.
   *
   * For a `FAMILY_ACTIVITY` item this is `null` for every non-admin BY DESIGN:
   * the backend never selects the amount or category columns for them, so
   * there is nothing to show. Do not "fix" an empty body by fetching the entry
   * — that would reintroduce exactly the leak the backend prevents.
   */
  body: string | null;
  createdAt: string;
  link: string | null;
  read: boolean;
}

export interface FeedResponse {
  items: NotificationItem[];
  unreadCount: number;
}

export async function fetchFeed(limit: number, unreadOnly = false): Promise<FeedResponse> {
  const res = await apiFetch(
    notificationUrl("", { limit, unreadOnly: unreadOnly ? "true" : "" }),
  );
  if (!res.ok) throw new Error(`Failed to load notifications (${res.status})`);
  return (await res.json()) as FeedResponse;
}

export async function fetchUnreadCount(): Promise<number> {
  const res = await apiFetch(notificationUrl("/unread-count"));
  if (!res.ok) throw new Error(`Failed to load unread count (${res.status})`);
  const body = (await res.json()) as { unreadCount: number };
  return body.unreadCount ?? 0;
}

export async function markRead(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const res = await apiFetch(notificationUrl("/read"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`Failed to mark read (${res.status})`);
}

export async function markAllRead(): Promise<void> {
  const res = await apiFetch(notificationUrl("/read-all"), { method: "POST" });
  if (!res.ok) throw new Error(`Failed to mark all read (${res.status})`);
}

// ── Push prefs (Wave 6, B4) ─────────────────────────────────────────────────
//
// Mirrors `frontend/mobile/src/lib/push/{types,api}.ts` field-for-field against the
// same `/py-api/v1/push/prefs` contract — per-type toggles, one global mute,
// and reset-to-defaults. Not promoted into `API_DOMAINS` for the same reason
// `notificationUrl` above is not: avoiding contention on `config.ts` while
// other agents are editing it this wave.

const PUSH: DomainConfig = { backend: "fastapi", root: "/push" };

function pushUrl(path = ""): string {
  const suffix = path && !path.startsWith("/") ? `/${path}` : path;
  return `${API_HOST}${BACKEND_PREFIX[PUSH.backend]}${PUSH.root}${suffix}`;
}

export interface PushPrefs {
  entries: boolean;
  rent: boolean;
  budget: boolean;
  backup: boolean;
  /** ISO-8601 UTC, or null. Server rejects anything more than 24h ahead. */
  muteUntil: string | null;
}

export interface PushPrefsPatch {
  entries?: boolean;
  rent?: boolean;
  budget?: boolean;
  backup?: boolean;
  clearMute?: boolean;
  muteUntil?: string;
}

export async function fetchPushPrefs(): Promise<PushPrefs> {
  const res = await apiFetch(pushUrl("/prefs"));
  if (!res.ok) throw new Error(`Failed to load notification settings (${res.status})`);
  return (await res.json()) as PushPrefs;
}

export async function updatePushPrefs(patch: PushPrefsPatch): Promise<PushPrefs> {
  const res = await apiFetch(pushUrl("/prefs"), {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => null);
    throw new Error(detail?.error ?? `Failed to save notification settings (${res.status})`);
  }
  return (await res.json()) as PushPrefs;
}

export async function resetPushPrefs(): Promise<PushPrefs> {
  const res = await apiFetch(pushUrl("/prefs/reset"), { method: "POST" });
  if (!res.ok) throw new Error(`Failed to reset notification settings (${res.status})`);
  return (await res.json()) as PushPrefs;
}
